/**
 * Which roles Event Hub renders for — the decision, as plain data.
 *
 * Normally the caller's own: the id token's LDAP groups, mapped by rolesOf. While an admin
 * impersonates, the service authorizes as the impersonated user, with THAT user's roles
 * (AWSECEventHub DefaultRequestAuthorizer), and the token says nothing about them. Rendering from
 * the token then would show the admin's view over the other user's data — buttons the service
 * will 403, and none of the ones it would allow. So the roles come from `GetCurrentUser` instead.
 *
 * Until that answer arrives there are no roles at all, not the admin's: a page rendered from the
 * admin's roles for a moment would flash actions the impersonated user does not have. The
 * answer is keyed by login, so one fetched for the previous target is never applied to the next.
 *
 * Kept free of React so it is covered by the unit tests Coverlay counts.
 */

import type { EventHubRole } from './authorization';

export interface EventHubRolesState {
  /** The roles to render for. Empty while loading or after a failure. */
  roles: ReadonlySet<EventHubRole>;
  /** True while the impersonated user's roles are being fetched. */
  loading: boolean;
  /** Why the impersonated user's roles could not be fetched, or null. */
  error: string | null;
}

/** What GetCurrentUser said, and for whom. */
export interface ServedRoles {
  login: string;
  roles: ReadonlySet<EventHubRole>;
}

const NONE: ReadonlySet<EventHubRole> = new Set();

export function effectiveRolesFor(input: {
  /** The caller's own roles, from the id token. */
  own: ReadonlySet<EventHubRole>;
  /** The login Event Hub acts as (useEventHubLogin). */
  login: string;
  /** True when that login is someone else's. */
  impersonating: boolean;
  /** The latest GetCurrentUser answer, or null. */
  served: ServedRoles | null;
  /** The latest GetCurrentUser failure, or null. */
  error: string | null;
}): EventHubRolesState {
  const { own, login, impersonating, served, error } = input;
  if (!impersonating) return { roles: own, loading: false, error: null };
  if (served && served.login === login) {
    return { roles: served.roles, loading: false, error: null };
  }
  if (error) return { roles: NONE, loading: false, error };
  return { roles: NONE, loading: true, error: null };
}
